import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { TaxCode, TaxSummaryEntry } from '@/types';


export function TaxSummary() {
  const { idToken } = useCognitoAuth();
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [taxEntries, setTaxEntries] = useState<TaxSummaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-03-31');

  useEffect(() => {
    if (!idToken) return;
    async function fetchData() {
      try {
        const [codes, summary] = await Promise.all([
          api.get<TaxCode[]>('/api/entities/tax-codes', { errorCode: ErrorCode.FETCH_TAX_SUMMARY, token: idToken! }),
          api.get<{ entries?: TaxSummaryEntry[] }>(`/api/tax-summary?start_date=${startDate}&end_date=${endDate}`, { errorCode: ErrorCode.FETCH_TAX_SUMMARY, token: idToken! }),
        ]);
        setTaxCodes(codes ?? []);
        setTaxEntries(summary.entries ?? []);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch tax summary');
        // Still try to fetch tax codes if tax summary fails
        try {
          const codes = await api.get<TaxCode[]>('/api/entities/tax-codes', { errorCode: ErrorCode.FETCH_TAX_SUMMARY, token: idToken! });
          setTaxCodes(codes ?? []);
        } catch {
          // ignore
        }
      } finally {
        setLoading(false);
      }
    }
    void fetchData();
  }, [startDate, endDate, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading tax summary...</p>
      </div>
    );
  }

  if (error && taxEntries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <i className="bi bi-exclamation-triangle text-3xl text-red-600 mb-4"></i>
        <p className="text-muted-foreground">Error: {error}</p>
      </div>
    );
  }

  const totalCollected = taxEntries.reduce((sum, e) => sum + Number(e.tax_collected ?? 0), 0);
  const totalPaid = taxEntries.reduce((sum, e) => sum + Number(e.tax_paid ?? 0), 0);
  const totalNet = taxEntries.reduce((sum, e) => sum + Number(e.net_tax ?? 0), 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tax Summary</h1>
          <p className="text-sm text-muted-foreground">Sales tax collected and paid</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="text-muted-foreground">to</span>
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print Report</Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        For the period {formatDate(startDate)} to {formatDate(endDate)}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tax Collected</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency(totalCollected)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Tax Paid (ITC)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(totalPaid)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardDescription>Net Tax Remittance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono ${totalNet >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(totalNet))}
              {totalNet >= 0 ? ' owed' : ' refund'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tax Code Summaries */}
      {taxEntries.filter(e => Number(e.tax_collected) > 0 || Number(e.tax_paid) > 0).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-percent"></i> Tax Summary by Code
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 px-3 text-left font-medium text-muted-foreground">Code</th>
                    <th className="py-2 px-3 text-left font-medium text-muted-foreground">Name</th>
                    <th className="py-2 px-3 text-right font-medium text-muted-foreground">Rate</th>
                    <th className="py-2 px-3 text-right font-medium text-muted-foreground">Collected</th>
                    <th className="py-2 px-3 text-right font-medium text-muted-foreground">Paid</th>
                    <th className="py-2 px-3 text-right font-medium text-muted-foreground">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {taxEntries.filter(e => Number(e.tax_collected) > 0 || Number(e.tax_paid) > 0).map((entry) => (
                    <tr key={entry.tax_code_id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-2 px-3 font-mono">{entry.code}</td>
                      <td className="py-2 px-3">{entry.name}</td>
                      <td className="py-2 px-3 text-right font-mono">{(Number(entry.rate) * 100).toFixed(1)}%</td>
                      <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(Number(entry.tax_collected))}</td>
                      <td className="py-2 px-3 text-right font-mono text-red-600">{formatCurrency(Number(entry.tax_paid))}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatCurrency(Number(entry.net_tax))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Total Remittance */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Total Tax Remittance</h3>
              <p className="text-sm text-muted-foreground">Amount to be remitted to CRA</p>
            </div>
            <div className="text-2xl font-bold font-mono">
              {formatCurrency(totalNet)}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" size="sm">View Filing History</Button>
            <Button variant="default" size="sm">Generate Return</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tax Codes Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-list-ul"></i> Active Tax Codes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Code</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Rate</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Type</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Province</th>
                </tr>
              </thead>
              <tbody>
                {taxCodes.filter(tc => tc.is_active).map((taxCode) => (
                  <tr key={taxCode.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{taxCode.code}</code></td>
                    <td className="py-2 px-3">{taxCode.name}</td>
                    <td className="py-2 px-3 font-mono">{(Number(taxCode.rate) * 100).toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <Badge variant={taxCode.type === 'HST' ? 'default' : taxCode.type === 'GST' ? 'secondary' : 'default'}>
                        {taxCode.type}
                      </Badge>
                    </td>
                    <td className="py-2 px-3">{taxCode.province ?? 'All'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
